import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { ArrowUpRight, FolderClosed, FolderOpen, FolderPlus, Sparkles, Check, MessageCircle, Apple, FileText, X } from "lucide-react";
import AskSopfie from "../components/AskSopfie";
import MyNutrition from "../components/MyNutrition";
import PersonalBook from "../components/PersonalBook";

export default function ClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Admin-only: create a folder for any client right from this page.
  const [showAdminCreate, setShowAdminCreate] = useState(false);
  const [adminClients, setAdminClients] = useState([]);
  const [adminTargetId, setAdminTargetId] = useState("");
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminError, setAdminError] = useState("");

  // testimonial submit
  const [tContent, setTContent] = useState("");
  const [tLabel, setTLabel] = useState("");
  const [tStatus, setTStatus] = useState(null); // {ok, msg}
  const [tBusy, setTBusy] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      // Admin viewing My space: load all assigned programs (we'll show a "all clients' folders" view).
      api.get("/admin/programs")
        .then((r) => setPrograms((r.data || []).filter((p) => p.client_id)))
        .catch(() => setPrograms([]))
        .finally(() => setLoading(false));
      api.get("/admin/clients").then((r) => {
        setAdminClients(r.data || []);
        if (!adminTargetId && r.data?.length) setAdminTargetId(r.data[0].id);
      });
    } else {
      api.get("/programs/me")
        .then((r) => setPrograms(r.data || []))
        .catch(() => setPrograms([]))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const adminCreateFolder = async (e) => {
    e.preventDefault();
    if (!adminTargetId) return;
    setAdminBusy(true);
    setAdminError("");
    try {
      const client = adminClients.find((c) => c.id === adminTargetId);
      const firstName = (client?.name || "").split(" ")[0] || "client";
      const { data } = await api.post("/admin/programs", {
        title: `${firstName}'s program`,
        description: null,
        client_id: adminTargetId,
        workouts: [],
      });
      navigate(`/programs/${data.id}`);
    } catch (err) {
      setAdminError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAdminBusy(false);
    }
  };

  const submitTestimonial = async (e) => {
    e.preventDefault();
    setTBusy(true);
    setTStatus(null);
    try {
      await api.post("/testimonials", {
        name: user.name,
        content: tContent,
        rating: 5,
        role_label: tLabel || null,
      });
      setTContent("");
      setTLabel("");
      setTStatus({ ok: true, msg: "Thank you. Sopfie will read it and approve it shortly." });
    } catch (e) {
      setTStatus({ ok: false, msg: formatApiError(e.response?.data?.detail) || e.message });
    } finally {
      setTBusy(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-[var(--bg)]" data-testid="client-dashboard">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="no-print">
          <p className="overline mb-3">Your space</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl text-[color:var(--ink)] tracking-tight">
            Hello {user?.name?.split(" ")[0]}. <em className="text-[color:var(--accent)] not-italic">Soft welcome.</em>
          </h1>
          <p className="mt-3 text-[color:var(--ink-2)] max-w-xl">
            Your numbers, your plan, your programs — and a quiet space to ask anything in between.
          </p>
        </div>

        {/* NUTRITION (top) */}
        <section className="mt-12" id="nutrition">
          <div className="mb-5 no-print">
            <p className="overline mb-3">From the inside</p>
            <h2 className="font-serif text-3xl text-[color:var(--ink)] tracking-tight flex items-center gap-2">
              <Apple size={20} strokeWidth={1.4} className="text-[color:var(--accent)]" />
              Your nutrition
            </h2>
            <p className="mt-2 text-sm text-[color:var(--ink-2)] max-w-xl">
              Save your numbers, pick the phase Sopfie has you in, then generate a plan built from foods you already eat.
            </p>
          </div>
          <MyNutrition />
        </section>

        {/* PROGRAM FOLDERS */}
        <section className="mt-20 no-print">
          <div className="flex items-end justify-between mb-2 flex-wrap gap-3">
            <h2 className="font-serif text-2xl text-[color:var(--ink)] flex items-center gap-2">
              <FolderClosed size={20} strokeWidth={1.4} className="text-[color:var(--accent)]" />
              {isAdmin ? "Program folders" : "Your program folders"}
            </h2>
            <div className="flex items-center gap-3">
              {isAdmin && (
                <button
                  onClick={() => setShowAdminCreate(true)}
                  className="btn-primary !py-1.5 !px-4 !text-xs"
                  data-testid="dashboard-new-folder"
                >
                  <FolderPlus size={13} strokeWidth={1.6} /> New folder
                </button>
              )}
              <span className="text-xs text-[color:var(--ink-3)]" data-testid="programs-count">{programs.length} folder{programs.length !== 1 ? "s" : ""}</span>
            </div>
          </div>
          <p className="text-sm text-[color:var(--ink-2)] mb-5 max-w-2xl">
            {isAdmin
              ? "All folders you've created for your clients. Open one to upload the program PDF and read the conversation."
              : "Each folder holds the program Sopfie sent you. Open it to read the PDF, post questions, share progress, or upload new notes — everything stays here so you can track your journey."
            }
          </p>

          {/* Admin: inline modal to pick a client and create a folder */}
          {isAdmin && showAdminCreate && (
            <form onSubmit={adminCreateFolder} className="card-soft p-6 mb-6 max-w-xl" data-testid="dashboard-new-folder-form">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <p className="overline">New folder</p>
                  <p className="font-serif text-2xl text-[color:var(--ink)] mt-1">Pick a client</p>
                  <p className="text-xs text-[color:var(--ink-3)] mt-1">A folder will be created and you'll land inside it to upload the program PDF.</p>
                </div>
                <button type="button" onClick={() => setShowAdminCreate(false)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]" data-testid="dashboard-new-folder-close">
                  <X size={16} />
                </button>
              </div>
              <select
                className="input-warm"
                value={adminTargetId}
                onChange={(e) => setAdminTargetId(e.target.value)}
                data-testid="dashboard-new-folder-client"
              >
                {adminClients.length === 0 && <option value="">No clients yet</option>}
                {adminClients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} · {c.email}</option>
                ))}
              </select>
              {adminError && <p className="mt-3 text-sm text-[color:var(--accent-2)]">{adminError}</p>}
              <div className="mt-4 flex justify-end gap-2">
                <button type="button" onClick={() => setShowAdminCreate(false)} className="btn-ghost !py-2 !px-4 !text-sm">Cancel</button>
                <button type="submit" disabled={!adminTargetId || adminBusy} className="btn-primary !py-2 !px-4 !text-sm" data-testid="dashboard-new-folder-submit">
                  {adminBusy ? "Creating…" : "Create & open folder"}
                </button>
              </div>
            </form>
          )}

          {loading ? (
            <p className="text-[color:var(--ink-3)] italic font-serif">Loading…</p>
          ) : programs.length === 0 ? (
            <div className="card-soft p-8" data-testid="programs-empty">
              <p className="font-serif text-xl text-[color:var(--ink)]">{isAdmin ? "No folders yet." : "No folder yet."}</p>
              <p className="text-[color:var(--ink-2)] mt-2 text-sm">
                {isAdmin
                  ? "Click \"New folder\" above to create one for any of your clients."
                  : "Sopfie will create your first program folder after your session. In the meantime, you can book one below."
                }
              </p>
              {!isAdmin && (
                <a href="https://youfit.planway.com/" target="_blank" rel="noreferrer" className="btn-primary mt-5" data-testid="empty-book-pt">
                  Book a session <ArrowUpRight size={16} strokeWidth={1.5} />
                </a>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {programs.map((p) => {
                const updateCount = (p.updates || []).length;
                const ownerName = isAdmin
                  ? (adminClients.find((c) => c.id === p.client_id)?.name || "Client")
                  : null;
                return (
                  <Link to={`/programs/${p.id}`} key={p.id} className="card-soft p-7 block group" data-testid={`program-${p.id}`}>
                    <div className="flex items-start gap-3">
                      <FolderOpen size={28} strokeWidth={1.3} className="text-[color:var(--accent)] mt-1 shrink-0 group-hover:scale-105 transition-transform" />
                      <div className="flex-1 min-w-0">
                        <p className="overline">{isAdmin ? `Folder · ${ownerName}` : "Program folder"}</p>
                        <h3 className="mt-1 font-serif text-2xl text-[color:var(--ink)] truncate">{p.title}</h3>
                        {p.description && <p className="mt-2 text-sm text-[color:var(--ink-2)] leading-relaxed line-clamp-2">{p.description}</p>}
                      </div>
                    </div>
                    <div className="mt-5 pt-4 border-t border-[color:var(--line-soft)] flex items-center justify-between gap-3 text-xs text-[color:var(--ink-3)]">
                      <div className="flex items-center gap-3 flex-wrap">
                        {(p.files && p.files.length > 0) ? (
                          <span className="inline-flex items-center gap-1.5 text-[color:var(--ink-2)]" data-testid={`program-${p.id}-has-pdf`}>
                            <FileText size={13} strokeWidth={1.5} /> {p.files.length} file{p.files.length !== 1 ? "s" : ""}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 italic">
                            <FileText size={13} strokeWidth={1.5} /> no files yet
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1.5 text-[color:var(--ink-2)]" data-testid={`program-${p.id}-notes`}>
                          <MessageCircle size={13} strokeWidth={1.5} /> {updateCount} note{updateCount !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <span className="text-[color:var(--accent)] group-hover:translate-x-0.5 transition-transform">Open folder →</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* PERSONAL BOOK (client-only) */}
        {!isAdmin && (
          <section className="mt-20 no-print" id="personal-book">
            <div className="mb-5">
              <p className="overline mb-3">A space for your journey</p>
              <h2 className="font-serif text-3xl text-[color:var(--ink)] tracking-tight">
                Your personal book.
              </h2>
              <p className="mt-2 text-sm text-[color:var(--ink-2)] max-w-2xl">
                Pictures of where you are, words for what you feel. When it's time, export it
                all as a downloadable book — or share it with someone who matters.
              </p>
            </div>
            <PersonalBook />
          </section>
        )}

        {/* SHARE STORY */}
        <section className="mt-20">
          <div className="grid md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
              <p className="overline mb-3">Share your story</p>
              <h2 className="font-serif font-light text-3xl text-[color:var(--ink)] tracking-tight">
                <Sparkles size={18} strokeWidth={1.4} className="inline text-[color:var(--accent)] mr-2 -mt-1" />
                Words can light the way for someone else.
              </h2>
              <p className="mt-4 text-[color:var(--ink-2)] leading-relaxed">
                If your journey wants to be shared, you can write a few lines here.
                Sopfie will read them and, with your permission, place them gently on the website.
              </p>
            </div>
            <form onSubmit={submitTestimonial} className="md:col-span-7 card-soft p-7" data-testid="testimonial-form">
              <label className="overline block mb-2">Your words</label>
              <textarea
                required minLength={4}
                value={tContent}
                onChange={(e) => setTContent(e.target.value)}
                rows={5}
                className="input-warm"
                placeholder="What has shifted for you? How does your body feel now?"
                data-testid="testimonial-content"
              />
              <label className="overline block mb-2 mt-4">A short label (optional)</label>
              <input
                value={tLabel}
                onChange={(e) => setTLabel(e.target.value)}
                placeholder="e.g. Personal Training Client"
                className="input-warm"
                data-testid="testimonial-label"
              />
              {tStatus && (
                <p className={`mt-3 text-sm rounded-lg px-3 py-2 border border-[color:var(--line-soft)] ${tStatus.ok ? "text-[color:var(--ink)] bg-[color:var(--bg-2)]" : "text-[color:var(--accent-2)] bg-[color:var(--bg-2)]"}`} data-testid="testimonial-status">
                  {tStatus.ok && <Check size={14} className="inline mr-1" strokeWidth={1.6} />}{tStatus.msg}
                </p>
              )}
              <button disabled={tBusy} className="btn-primary mt-5" data-testid="testimonial-submit">
                {tBusy ? "Sending…" : "Send to Sopfie"}
              </button>
            </form>
          </div>
        </section>

        {/* ASK SOPFIE — AI (bottom) */}
        <section className="mt-20" id="ask-sopfie">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
            <div>
              <p className="overline mb-3">A space for your questions</p>
              <h2 className="font-serif text-3xl text-[color:var(--ink)] tracking-tight flex items-center gap-2">
                <MessageCircle size={20} strokeWidth={1.4} className="text-[color:var(--accent)]" />
                Ask Sopfie
              </h2>
              <p className="mt-2 text-sm text-[color:var(--ink-2)] max-w-xl">
                A presence between sessions. Share what your body is feeling — physical or emotional —
                and I'll meet you here, just as I would in person.
              </p>
            </div>
          </div>
          <AskSopfie />
        </section>
      </div>
    </div>
  );
}
